import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/DropDownLoggedInHeader.module.css";
import styled, { keyframes, css } from "styled-components";
import { useSendLogoutMutation } from "../features/auth/authApiSlice";
import useAuth from "../hooks/useAuth";
import { useGetUsersQuery } from "../features/users/usersApiSlice";
import { useGetCompaniesQuery } from "../features/companies/companyApiSlice";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HistoryIcon from '@mui/icons-material/History';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { shouldShowAI } from "../utils/aiConfig";

import { USER_ROLE_KEYS } from "../config/userRoles";
import UserImage from "../features/documents/UserImage";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@mui/material";

let scaleZ = keyframes`{
    0% {
        opacity: 0;
        transform: scale(0);
    }

    80% {
        transform: scale(1.07);
    }

    100% {
        opacity: 1;
        transform: scale(1);
    }
}`;

const DropDownItem = styled.div`
  display: flex;
  height: 40px;
  padding: 0px 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  border-radius: 5px;

  opacity: 0;
  cursor: pointer;

  &:hover {
    background: #f9f1f1;
  }

  ${(props) =>
    props.active &&
    css`
      background: #c2ffed;
    `}

  transform-origin: top center;

  ${(props) =>
    props.index &&
    css`
      animation: ${scaleZ} 300ms ${60 * props.index}ms ease-in-out forwards;
    `}
`;

export default function DropDownLoggedInHeader({ mobileView, setToggleModal }) {
  const { t } = useTranslation(["Header"]);
  const navigate = useNavigate();
  const { id, company: companyId } = useAuth();
  const dropdownRef = useRef(null);

  const { user } = useGetUsersQuery("usersList", {
    selectFromResult: ({ data }) => ({
      user: data?.entities[id],
    }),
  });

  // const user = useSelector((state) => selectUserById(state, id));

  const { company, isLoadingCompany } = useGetCompaniesQuery("companiesList", {
    selectFromResult: ({ data, isLoading }) => ({
      company: data?.entities[user?.company || companyId],
      isLoadingCompany: isLoading,
    }),
  });

  // const company = useSelector((state) => selectCompanyById(state, companyId));

  const [
    sendLogout,
    {
      isSuccess,
      // isLoading, isError, error
    },
  ] = useSendLogoutMutation();

  useEffect(() => {
    if (isSuccess) navigate("/");
  }, [isSuccess, navigate]);

  const [toggleDropdown, setToggleDropdown] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toggleDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setToggleDropdown(false);
      }
    };

    // Delay attaching the event listener
    const eventListenerTimeout = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    // Cleanup the event listener on component unmount
    return () => {
      clearTimeout(eventListenerTimeout);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [toggleDropdown]);

  const menuOptions = [
    {
      title: "profile",
      // icon: <PermIdentityIcon/>,
      icon_src: "/icons/bidd/profile_icon_dropdown.svg",
    },
    {
      title: "messages",
      // icon: <CommentIcon/>,
      icon_src: "/icons/bidd/message_icon_dropdown.svg",
    },
    // Conditionally add the "Stillingar" option if the user is validated
    ...(user?.isValidated && user?.userRole === USER_ROLE_KEYS.MasterUser
      ? [
        {
          title: "users",
          // icon: <SettingsIcon/>,
          icon_src: "/icons/bidd/settings_icon_dropdown.svg",
        },
      ]
      : []),

    //TODO bæta við seinna
    // {
    //   title: "Notendur",
    //   // icon: <GroupAddIcon/>,
    //   icon_src: "/icons/bidd/history_icon_dropdown.svg",
    // },
    // {
    //   title: "Mín útboð",
    //   icon_src: "/icons/bidd/history_icon_dropdown.svg",
    // },

    {
      title: "calendar",
      icon: <CalendarMonthIcon sx={{ width: "16px" }} />,
      // icon_src: "/icons/bidd/logout_icon_dropdown.svg",
    },

    ...(user?.isValidated
      ? [
        {
          title: "activityLogs",
          icon: <HistoryIcon sx={{ width: "16px" }} />,
          // icon_src: "/icons/bidd/history_icon_dropdown.svg",
        },
        ...(shouldShowAI() ? [{
          title: "aiAssistant",
          icon: <SmartToyIcon sx={{ width: "16px" }} />,
          // icon_src: "/icons/bidd/ai_assistant_icon_dropdown.svg",
        }] : []),
      ]
      : []),

    {
      title: "settings",
      // icon: <CalendarMonthIcon sx={{ width: "16px" }} />,
      icon_src: "/icons/bidd/settings_icon_dropdown.svg",
    },

    {
      title: "signOut",
      // icon: <LogoutIcon/>,
      icon_src: "/icons/bidd/logout_icon_dropdown.svg",
    },
  ];

  useEffect(() => {
    setToggleDropdown(mobileView);
  }, [mobileView]);

  return (
    <>
      <div
        className={styles.userReviewRatingDropDown}
        onClick={() => {
          setToggleDropdown(!toggleDropdown);
        }}
      >
        {isLoadingCompany ? (
          <Skeleton
            variant="circular"
            width="45px"
            height="45px"
            sx={{ backgroundColor: "rgb(51,51,51)" }}
          />
        ) : (
          <UserImage
            image={company?.photo_url}
            mobileView={mobileView}
            toggleDropdown={toggleDropdown}
          />
        )}
      </div>

      {toggleDropdown && (
        <div className={styles.dropdownRatingMenu} ref={dropdownRef}>
          <div className={styles.itemsContainer}>
            {menuOptions.map((menuOption, index) => (
              <DropDownItem
                key={index}
                index={index + 1}
                onClick={
                  () => {
                    setToggleDropdown(!toggleDropdown);

                    if (menuOption.title === "signOut") {
                      // remove profile tab from local storage

                      localStorage.removeItem("profileTab");
                      localStorage.removeItem("companySettingsTab");

                      sendLogout();
                    } else if (menuOption.title === "profile") {
                      navigate("/minar-sidur/profile");
                    } else if (menuOption.title === "messages") {
                      navigate("/minar-sidur/chats");
                      // } else if (menuOption.title === "Notendur") {
                      //   navigate("/minar-sidur/company-users");
                    } else if (menuOption.title === "users") {
                      navigate("/minar-sidur/company-settings");
                      // if (employeeRole) {
                      //   navigate("/minar-sidur/company-about");
                      // } else if (managerRole) {
                      //   navigate("/minar-sidur/institution-about");
                      // }
                    } else if (menuOption.title === "calendar") {
                      navigate("/minar-sidur/calendar");
                    } else if (menuOption.title === "activityLogs") {
                      navigate("/minar-sidur/activity-logs");
                    } else if (menuOption.title === "aiAssistant") {
                      navigate("/minar-sidur/ai-assistant");
                    } else if (menuOption.title === "settings") {
                      navigate("/minar-sidur/settings");
                    }
                    typeof setToggleModal === "function" &&
                      setToggleModal(false);
                  }
                  // setOption(menuOption);
                }
              //   active={menuOption.name === option.name}
              >
                {/* item Icon */}

                {React.isValidElement(menuOption.icon) ? (
                  menuOption.icon
                ) : (
                  <img src={menuOption.icon_src} alt={menuOption.title} />
                )}

                {/* item title */}
                <div className={styles.itemTitle}>
                  {t(`menuOptions.${menuOption.title}`)}
                </div>
              </DropDownItem>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

